import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../images/logo.png';
import buttonCircle from '../images/buttoncircle.png';

function Navbarr({ scrollToTrainers, scrollToAbout, scrollToShop, scrollToContact, scrollToTrainingZone }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const location = useLocation();

  // Define responsive breakpoints first before using them in hooks
  const isMobile = windowWidth <= 480;
  const isMediumMobile = windowWidth >= 481 && windowWidth <= 767;
  const isLargePhone = windowWidth >= 768 && windowWidth <= 991;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('nav') && !event.target.closest('button')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [menuOpen]);

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Control body scroll when menu is open
  useEffect(() => {
    if (isMobile || isMediumMobile) {
      if (menuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [menuOpen, isMobile, isMediumMobile]);

  // Animated Hamburger Component
  const AnimatedHamburger = ({ isOpen, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    const hamburgerStyles = {
      container: {
        width: '50px',
        height: '50px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: isOpen ? 'rgba(0,0,0,0.7)' : 'transparent',
        border: isOpen ? '1px solid rgba(255,255,255,0.2)' : 'none',
        padding: '12px',
        zIndex: 10000,
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        backgroundColor: isHovered ? 'rgba(255,255,255,0.1)' : (isOpen ? 'rgba(0,0,0,0.7)' : 'transparent'),
        position: 'relative',
      },
      lineContainer: {
        width: '30px',
        height: '22px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      },
      line: {
        width: '100%',
        height: '3px',
        backgroundColor: 'white',
        borderRadius: '2px',
        transition: 'all 0.3s ease-in-out',
        transformOrigin: 'center',
        position: 'absolute',
      },
      lineTop: {
        top: '0px',
        transform: isOpen
          ? 'translateY(9.5px) rotate(45deg)'
          : 'translateY(0px) rotate(0deg)',
        backgroundColor: isOpen ? '#fff' : '#fff',
      },
      lineMiddle: {
        top: '9.5px',
        opacity: isOpen ? 0 : 1,
        transform: isOpen ? 'scale(0)' : 'scale(1)',
        backgroundColor: isOpen ? '#fff' : '#fff',
      },
      lineBottom: {
        top: '19px',
        transform: isOpen
          ? 'translateY(-9.5px) rotate(-45deg)'
          : 'translateY(0px) rotate(0deg)',
        backgroundColor: isOpen ? '#fff' : '#fff',
      },
    };

    return (
      <button
        style={hamburgerStyles.container}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        <div style={hamburgerStyles.lineContainer}>
          <div style={{ ...hamburgerStyles.line, ...hamburgerStyles.lineTop }}></div>
          <div style={{ ...hamburgerStyles.line, ...hamburgerStyles.lineMiddle }}></div>
          <div style={{ ...hamburgerStyles.line, ...hamburgerStyles.lineBottom }}></div>
        </div>
      </button>
    );
  };

  const styles = {
    header: {
      width: '100%',
      padding: (isMobile || isMediumMobile) ? '15px' :
        isLargePhone ? '15px 35px' :
          isTablet ? '18px 40px' :
            '20px 50px',
      boxSizing: 'border-box',
      backgroundColor: menuOpen && (isMobile || isMediumMobile) ? 'rgba(0,0,0,0.97)' : 'transparent',
      backdropFilter: menuOpen && (isMobile || isMediumMobile) ? 'blur(5px)' : 'none',
      position: menuOpen && (isMobile || isMediumMobile) ? 'fixed' : 'absolute',
      top: 0,
      left: 0,
      zIndex: 9999,
      transition: 'background-color 0.3s ease',
    },
    nav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative',
      width: '100%',
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: (isMobile || isMediumMobile) ? '0' :
        isLargePhone ? '25px' :
          isTablet ? '30px' :
            '50px',
      marginTop: (isMobile || isMediumMobile) ? '0' :
        isLargePhone ? '12px' :
          isTablet ? '15px' :
            '20px',
    },
    logo: {
      height: isMobile ? '80px' :
        isMediumMobile ? '90px' :
          isLargePhone ? '125px' :
            isTablet ? '135px' :
              '130px',
      width: isMobile ? '90px' :
        isMediumMobile ? '100px' :
          isLargePhone ? '135px' :
            isTablet ? '145px' :
              '140px',
    },
    menuIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '0',
      background: 'transparent',
      border: 'none',
      padding: '10px',
      borderRadius: '8px',
      transition: 'background-color 0.2s ease',
      position: 'relative',
      zIndex: 1002,
    },
    menuList: {
      listStyle: 'none',
      display: (isMobile || isMediumMobile) ? 'flex' : 'flex',
      flexDirection: (isMobile || isMediumMobile) ? 'column' : 'row',
      gap: (isMobile || isMediumMobile) ? '0' :
        isLargePhone ? '25px' :
          isTablet ? '35px' :
            '30px',
      position: (isMobile || isMediumMobile) ? 'fixed' : 'static',
      top: (isMobile || isMediumMobile) ? '0' : undefined,
      left: (isMobile || isMediumMobile) ? '0' : undefined,
      right: (isMobile || isMediumMobile) ? '0' : undefined,
      bottom: (isMobile || isMediumMobile) ? '0' : undefined,
      width: (isMobile || isMediumMobile) ? '100vw' : 'auto',
      height: (isMobile || isMediumMobile) ? '100vh' : 'auto',
      backgroundColor: (isMobile || isMediumMobile) ? 'rgba(0,0,0,0.97)' : 'transparent',
      backdropFilter: (isMobile || isMediumMobile) ? 'blur(5px)' : 'none',
      padding: (isMobile || isMediumMobile) ? '100px 20px 120px 20px' : 0, // Increased padding for content
      overflowY: (isMobile || isMediumMobile) ? 'auto' : 'visible', // Allow scrolling in mobile menu
      zIndex: (isMobile || isMediumMobile) ? 9998 : 9998,
      transition: 'all 0.3s ease-in-out',
      transform: (isMobile || isMediumMobile) ? (menuOpen ? 'translateY(0)' : 'translateY(-100vh)') : 'none',
      opacity: (isMobile || isMediumMobile) ? (menuOpen ? 1 : 0) : 1,
      visibility: (isMobile || isMediumMobile) ? (menuOpen ? 'visible' : 'hidden') : 'visible',
      boxShadow: 'none',
      border: 'none',
      justifyContent: (isMobile || isMediumMobile) ? 'flex-start' : 'center',
      alignItems: (isMobile || isMediumMobile) ? 'center' : 'center',
      textAlign: (isMobile || isMediumMobile) ? 'center' : 'left',
    },
    menuItem: {
      margin: (isMobile || isMediumMobile) ? '20px 0' : 0,
      width: (isMobile || isMediumMobile) ? '100%' : 'auto',
      textAlign: (isMobile || isMediumMobile) ? 'center' : 'left',
    },
    link: {
      color: 'white',
      fontWeight: 600,
      fontSize: isMobile ? '20px' :
        isMediumMobile ? '22px' :
          isLargePhone ? '17px' :
            isTablet ? '18px' :
              '17px',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      textDecoration: 'none',
      display: 'block',
      padding: (isMobile || isMediumMobile) ? '12px 0' : '0',
      letterSpacing: (isMobile || isMediumMobile) ? '1px' : 'normal',
    },
    joinNowButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: '#333',
      border: '2px solid #fff',
      borderRadius: '50px',
      padding: isLargePhone ? '8px 18px' :
        isTablet ? '12px 22px' :
          '10px 20px',
      color: '#fff',
      fontSize: isLargePhone ? '15px' :
        isTablet ? '17px' :
          '16px',
      cursor: 'pointer',
      boxShadow: '4px 4px 0 #000',
      fontWeight: 'bold',
      fontFamily: 'Arial, sans-serif',
      whiteSpace: 'nowrap',
      marginTop: (isMobile || isMediumMobile) ? '12px' : 0,
    },
    buttonText: {
      marginRight: isLargePhone ? '18px' :
        isTablet ? '22px' :
          '20px',
    },
    buttonCircle: {
      width: isLargePhone ? '32px' :
        isTablet ? '38px' :
          '35px',
      height: isLargePhone ? '32px' :
        isTablet ? '38px' :
          '35px',
    },
    circleImage: {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
    },
  };

  const handleMenuClick = (target) => {
    // Close menu first for mobile
    if (menuOpen) setMenuOpen(false);

    switch (target) {
      case 'about':
        if (location.pathname !== '/') {
          navigate('/');
          // Wait for navigation to complete before scrolling
          setTimeout(() => {
            scrollToAbout && scrollToAbout();
          }, 100);
        } else {
          scrollToAbout && scrollToAbout();
        }
        break;
      case 'trainers':
        if (location.pathname !== '/') {
          navigate('/');
          setTimeout(() => {
            scrollToTrainers && scrollToTrainers();
          }, 100);
        } else {
          // Check if we should navigate to trainers page or scroll to trainers section
          navigate('/trainers');
        }
        break;
      case 'shop':
        navigate('/shop');
        break;
      case 'contact':
        if (location.pathname !== '/') {
          navigate('/');
          setTimeout(() => {
            scrollToContact && scrollToContact();
          }, 100);
        } else {
          scrollToContact && scrollToContact();
        }
        break;
      case 'locations':
        if (location.pathname !== '/') {
          navigate('/');
          setTimeout(() => {
            scrollToTrainingZone && scrollToTrainingZone();
          }, 100);
        } else {
          scrollToTrainingZone && scrollToTrainingZone();
        }
        break;
      default:
        break;
    }
  };

  const renderMenuItems = () => (
    <>
      {['about', 'trainers', 'shop', 'locations', 'contact'].map((itemKey) => {
        const label = {
          about: 'About Us',
          trainers: 'Trainers',
          shop: 'Shop',
          contact: 'Contact Us',
          locations: 'Locations',
        }[itemKey];

        return (
          <li key={itemKey} style={styles.menuItem}>
            <span
              style={styles.link}
              onClick={() => handleMenuClick(itemKey)}
              onMouseEnter={(e) => (e.target.style.color = '#aaff00')}
              onMouseLeave={(e) => (e.target.style.color = 'white')}
            >
              {label}
            </span>
          </li>
        );
      })}
    </>
  );

  return (
    <header style={styles.header}>
      <nav style={styles.nav}>
        <div style={styles.logoContainer}>
          <img src={logo} alt="Logo" style={styles.logo} />
        </div>

        {(isMobile || isMediumMobile) && (
          <>
            <div style={{
              ...styles.menuIcon,
              position: 'fixed',
              top: '15px',
              right: '15px',
              zIndex: 10000,
              backgroundColor: menuOpen ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
              borderRadius: '50%'
            }}>
              <AnimatedHamburger
                isOpen={menuOpen}
                onClick={() => setMenuOpen(!menuOpen)}
              />
            </div>
            <ul style={styles.menuList}>
              {renderMenuItems()}
              <li style={{ ...styles.menuItem, marginTop: '40px' }}>
                <button
                  style={{
                    ...styles.joinNowButton,
                    margin: '0 auto',
                    display: 'flex',
                    padding: '14px 24px',
                    fontSize: '18px',
                    background: 'linear-gradient(90deg, #333 0%, #444 100%)'
                  }}
                  onClick={() => {
                    setMenuOpen(false);
                    if (location.pathname !== '/') {
                      navigate('/');
                      setTimeout(() => {
                        scrollToContact && scrollToContact();
                      }, 100);
                    } else {
                      scrollToContact && scrollToContact();
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#555';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '2px 2px 0 #000';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(90deg, #333 0%, #444 100%)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '4px 4px 0 #000';
                  }}
                >
                  <span style={styles.buttonText}>Join Us Now</span>
                  <span style={styles.buttonCircle}>
                    <img src={buttonCircle} alt="Button Circle" style={styles.circleImage} />
                  </span>
                </button>
              </li>
            </ul>
          </>
        )}

        {!(isMobile || isMediumMobile) && (
          <>
            <ul style={styles.menuList}>{renderMenuItems()}</ul>
            <button
              style={styles.joinNowButton}
              onClick={() => {
                if (location.pathname !== '/') {
                  navigate('/');
                  setTimeout(() => {
                    scrollToContact && scrollToContact();
                  }, 100);
                } else {
                  scrollToContact && scrollToContact();
                }
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '2px 2px 0 #000';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#333';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '4px 4px 0 #000';
              }}
            >
              <span style={styles.buttonText}>Join Us Now</span>
              <span style={styles.buttonCircle}>
                <img src={buttonCircle} alt="Button Circle" style={styles.circleImage} />
              </span>
            </button>
          </>
        )}
      </nav>
    </header>
  );
}

export default Navbarr;
